import { Controller } from "@hotwired/stimulus"
import axios from "axios"

// Personalizacja lewego sidebara "Podręczne" (widok /toolkit/ui/sidebar).
// Łączy drag&drop kolejności z checkboxami widoczności. Po każdej zmianie (upuszczenie
// lub przełączenie checkboxa) zapisuje pełną listę [{ key, on }] w kolejności DOM-u
// przez /account/set_field do wskazanego target: "user_setting" (układ osobisty) lub
// "account" (domyślny układ konta, tylko admin). Reset (value="default") kasuje klucz —
// user spada wtedy na układ konta, konto na domyślny systemowy.
// Rejestrowany w controllers/index_account.js (ładowany w widokach /account), nie w globalnym manifeście.
//
// Wymagane data-attributes na kontenerze:
//   data-controller="sidebar-menu"
//   data-sidebar-menu-save-url-value="<%= account_set_field_path %>"
//   data-sidebar-menu-key-value="intum.sidebar.items"
//   data-sidebar-menu-save-target-value="user_setting" | "account"
//   data-sidebar-menu-reset-confirm-value="<%= tt(...) %>"
//   data-action="dragstart->... dragover->... drop->... dragend->..."
// Każdy wiersz: draggable="true", data-sidebar-menu-target="item", data-key="modul:code",
// z checkboxem data-action="change->sidebar-menu#toggle".
export default class extends Controller {
  static targets = ["item"]
  static values = { saveUrl: String, key: String, resetKey: String, saveTarget: String, resetConfirm: String }

  dragstart(event) {
    const item = event.target.closest("[data-sidebar-menu-target='item']")
    if (!item) {
      event.preventDefault()
      return
    }
    this.draggedItem = item
    event.dataTransfer.effectAllowed = "move"
    item.classList.add("opacity-40")
  }

  dragover(event) {
    if (!this.draggedItem) return
    event.preventDefault()

    const target = event.target.closest("[data-sidebar-menu-target='item']")
    if (!target || target === this.draggedItem) return

    const rect = target.getBoundingClientRect()
    const insertBefore = event.clientY < rect.top + rect.height / 2
    if (insertBefore) {
      target.parentNode.insertBefore(this.draggedItem, target)
    } else {
      target.parentNode.insertBefore(this.draggedItem, target.nextSibling)
    }
  }

  drop(event) {
    event.preventDefault()
  }

  dragend() {
    if (!this.draggedItem) return
    this.draggedItem.classList.remove("opacity-40")
    this.draggedItem = null
    this.save()
  }

  toggle() {
    this.save()
  }

  // checkbox "własny układ": zaznaczenie przejmuje bieżący (dziedziczony) układ jako własny,
  // odznaczenie (po potwierdzeniu) kasuje własny układ i wraca do dziedziczenia.
  toggleOwn(event) {
    const checkbox = event.target
    if (checkbox.checked) {
      this.adopt()
      return
    }

    if (this.resetConfirmValue && !window.confirm(this.resetConfirmValue)) {
      checkbox.checked = true // anulowano — przywróć stan włączony
      return
    }
    this.resetToDefault()
  }

  // materializuje bieżący (dziedziczony) układ jako własny na wskazanym target,
  // potem wraca na zakładkę "Pasek boczny", żeby lista stała się edytowalna.
  adopt() {
    axios.post(this.saveUrlValue, null, {
      params: {
        target: this.saveTargetValue || "user_setting",
        key: this.keyValue,
        value: JSON.stringify(this.collectItems()),
      },
      headers: { Accept: "application/json" }
    }).then(() => {
      this.reloadToSidebar()
    }).catch((error) => {
      console.error("sidebar menu adopt failed", error)
    })
  }

  // przywraca układ domyślny: kasuje cały klucz intum.sidebar (układ + lupka klientów)
  // na wskazanym target. User → spada na domyślny układ konta, konto → na domyślny
  // systemowy. Wraca na zakładkę "Pasek boczny", żeby pokazać odziedziczony układ.
  resetToDefault() {
    axios.post(this.saveUrlValue, null, {
      params: {
        target: this.saveTargetValue || "user_setting",
        key: this.resetKeyValue || this.keyValue,
        value: "default",
      },
      headers: { Accept: "application/json" }
    }).then(() => {
      this.reloadToSidebar()
    }).catch((error) => {
      console.error("sidebar menu reset failed", error)
    })
  }

  // zbiera aktualną kolejność i stan checkboxów, zapisuje całą listę [{ key, on }]
  save() {
    axios.post(this.saveUrlValue, null, {
      params: {
        target: this.saveTargetValue || "user_setting",
        key: this.keyValue,
        value: JSON.stringify(this.collectItems()),
      },
      headers: { Accept: "application/json" }
    }).catch((error) => {
      console.error("sidebar menu save failed", error)
    })
  }

  collectItems() {
    return this.itemTargets.map((el) => ({
      key: el.dataset.key,
      // brak checkboxa (nietypowy wiersz) traktujemy jako widoczny, zamiast wywalać cały zapis
      on: el.querySelector("input[type=checkbox]")?.checked ?? true,
    }))
  }

  // przeładowuje stronę paska bocznego z parametrem, który otwiera właściwy wariant
  // (Dla mnie / Dla konta) — patrz sidebar_scope connect()
  reloadToSidebar() {
    const scope = this.saveTargetValue === "account" ? "account" : "user"
    window.location.href = `${window.location.pathname}?scope=${scope}`
  }
}
